import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  ReactNode,
} from 'react';
import {
  PlaybackState,
  PlaybackStatus,
  PlayerAction,
  PlayerItem,
  Duration,
  initialPlaybackState,
} from '../types/player.types';

// Helper function to find scene index based on position
function findSceneIndex(
  clips: PlayerItem['clips'],
  clipIndex: number,
  position: Duration
): number {
  const clip = clips[clipIndex];
  if (!clip) return 0;

  for (let i = 0; i < clip.scenes.length; i++) {
    if (position < clip.scenes[i].endTime) {
      return i;
    }
  }
  return Math.max(0, clip.scenes.length - 1);
}

// Player reducer
function playerReducer(state: PlaybackState, action: PlayerAction): PlaybackState {
  switch (action.type) {
    case 'LOAD_GBOX':
      return {
        ...initialPlaybackState,
        status: PlaybackStatus.Loading,
      };

    case 'CONTENT_LOADED': {
      const clip = action.playerItem.clips[0];
      return {
        ...state,
        status: PlaybackStatus.Paused,
        currentItem: action.playerItem,
        currentClipIndex: 0,
        currentSceneIndex: 0,
        displayedSceneIndex: 0,
        isDesynchronized: false,
        position: 0,
        clipDuration: clip?.totalDuration || 0,
        errorMessage: undefined,
      };
    }

    case 'PLAY':
      if (state.status === PlaybackStatus.Completed) {
        // Restart from beginning
        return {
          ...state,
          status: PlaybackStatus.Playing,
          currentClipIndex: 0,
          currentSceneIndex: 0,
          displayedSceneIndex: 0,
          position: 0,
          clipDuration: state.currentItem?.clips[0]?.totalDuration || 0,
        };
      }
      return { ...state, status: PlaybackStatus.Playing };

    case 'PAUSE':
      return { ...state, status: PlaybackStatus.Paused, showControls: true };

    case 'TOGGLE_PLAY_PAUSE':
      if (state.status === PlaybackStatus.Playing) {
        return { ...state, status: PlaybackStatus.Paused, showControls: true };
      }
      if (state.status === PlaybackStatus.Paused || state.status === PlaybackStatus.Completed) {
        if (state.status === PlaybackStatus.Completed) {
          return {
            ...state,
            status: PlaybackStatus.Playing,
            currentClipIndex: 0,
            currentSceneIndex: 0,
            displayedSceneIndex: 0,
            position: 0,
            clipDuration: state.currentItem?.clips[0]?.totalDuration || 0,
          };
        }
        return { ...state, status: PlaybackStatus.Playing };
      }
      return state;

    case 'SEEK_TO': {
      const clip = state.currentItem?.clips[state.currentClipIndex];
      if (!clip) return state;

      const position = Math.max(0, Math.min(action.position, clip.totalDuration));
      const newSceneIndex = findSceneIndex(
        state.currentItem?.clips || [],
        state.currentClipIndex,
        position
      );

      return {
        ...state,
        position,
        currentSceneIndex: newSceneIndex,
        displayedSceneIndex: newSceneIndex,
        isDesynchronized: false,
      };
    }

    case 'POSITION_UPDATED': {
      const clip = state.currentItem?.clips[state.currentClipIndex];
      if (!clip) return state;

      const newSceneIndex = findSceneIndex(
        state.currentItem?.clips || [],
        state.currentClipIndex,
        action.position
      );

      // Detect natural scene change (audio crossed boundary)
      const sceneChangedNaturally = newSceneIndex !== state.currentSceneIndex;

      // Auto-resync when audio advances to new scene while desynchronized
      const shouldResync = sceneChangedNaturally && state.isDesynchronized;

      return {
        ...state,
        position: action.position,
        currentSceneIndex: newSceneIndex,
        displayedSceneIndex: shouldResync
          ? newSceneIndex // Resync on natural scene change
          : state.isDesynchronized
            ? state.displayedSceneIndex // Keep user's manual selection
            : newSceneIndex, // Auto-advance if synchronized
        isDesynchronized: shouldResync ? false : state.isDesynchronized,
      };
    }

    case 'NEXT_SCENE': {
      const clip = state.currentItem?.clips[state.currentClipIndex];
      if (!clip) return state;

      const nextIndex = Math.min(
        state.displayedSceneIndex + 1,
        clip.scenes.length - 1
      );
      return {
        ...state,
        displayedSceneIndex: nextIndex,
        isDesynchronized: nextIndex !== state.currentSceneIndex,
      };
    }

    case 'PREVIOUS_SCENE': {
      const prevIndex = Math.max(state.displayedSceneIndex - 1, 0);
      return {
        ...state,
        displayedSceneIndex: prevIndex,
        isDesynchronized: prevIndex !== state.currentSceneIndex,
      };
    }

    case 'JUMP_TO_SCENE': {
      const clip = state.currentItem?.clips[state.currentClipIndex];
      if (!clip) return state;

      const sceneIndex = Math.max(
        0,
        Math.min(action.sceneIndex, clip.scenes.length - 1)
      );
      return {
        ...state,
        displayedSceneIndex: sceneIndex,
        isDesynchronized: sceneIndex !== state.currentSceneIndex,
      };
    }

    case 'NEXT_CLIP': {
      if (!state.currentItem) return state;

      const nextClipIndex = state.currentClipIndex + 1;
      if (nextClipIndex >= state.currentItem.clips.length) {
        return {
          ...state,
          status: PlaybackStatus.Completed,
          showControls: true,
        };
      }

      const nextClip = state.currentItem.clips[nextClipIndex];
      return {
        ...state,
        currentClipIndex: nextClipIndex,
        currentSceneIndex: 0,
        displayedSceneIndex: 0,
        isDesynchronized: false,
        position: 0,
        clipDuration: nextClip?.totalDuration || 0,
      };
    }

    case 'PREVIOUS_CLIP': {
      if (!state.currentItem) return state;

      const prevClipIndex = Math.max(0, state.currentClipIndex - 1);
      const prevClip = state.currentItem.clips[prevClipIndex];

      return {
        ...state,
        currentClipIndex: prevClipIndex,
        currentSceneIndex: 0,
        displayedSceneIndex: 0,
        isDesynchronized: false,
        position: 0,
        clipDuration: prevClip?.totalDuration || 0,
      };
    }

    case 'JUMP_TO_CLIP': {
      if (!state.currentItem) return state;

      const clipIndex = Math.max(
        0,
        Math.min(action.clipIndex, state.currentItem.clips.length - 1)
      );
      const clip = state.currentItem.clips[clipIndex];
      const sceneIndex = action.sceneIndex ?? 0;

      return {
        ...state,
        currentClipIndex: clipIndex,
        currentSceneIndex: sceneIndex,
        displayedSceneIndex: sceneIndex,
        isDesynchronized: false,
        position: clip?.scenes[sceneIndex]?.startTime || 0,
        clipDuration: clip?.totalDuration || 0,
        audioError: undefined, // Clear audio error when changing clips
      };
    }

    case 'CLIP_COMPLETED': {
      if (!state.currentItem) return state;

      const isLastClip =
        state.currentClipIndex >= state.currentItem.clips.length - 1;
      if (isLastClip) {
        return {
          ...state,
          status: PlaybackStatus.Completed,
          showControls: true,
        };
      }

      // Auto-advance to next clip
      const nextClipIndex = state.currentClipIndex + 1;
      const nextClip = state.currentItem.clips[nextClipIndex];
      return {
        ...state,
        currentClipIndex: nextClipIndex,
        currentSceneIndex: 0,
        displayedSceneIndex: 0,
        isDesynchronized: false,
        position: 0,
        clipDuration: nextClip?.totalDuration || 0,
      };
    }

    case 'AUDIO_ENDED': {
      // Audio finished but scenes continue playing via silent timer
      // No state change needed - position updates will continue from useAudio
      return state;
    }

    case 'SCENE_DISPLAY_CHANGED':
      return {
        ...state,
        displayedSceneIndex: action.sceneIndex,
        isDesynchronized: action.sceneIndex !== state.currentSceneIndex,
      };

    case 'SET_VOLUME':
      return {
        ...state,
        volume: Math.max(0, Math.min(1, action.volume)),
        isMuted: action.volume === 0,
      };

    case 'TOGGLE_MUTE':
      return { ...state, isMuted: !state.isMuted };

    case 'SET_SPEED':
      return {
        ...state,
        speed: Math.max(0.25, Math.min(2, action.speed)),
      };

    case 'MINIMIZE':
      return { ...state, isMinimized: true };

    case 'MAXIMIZE':
      return { ...state, isMinimized: false };

    case 'TOGGLE_CONTROLS':
      return { ...state, showControls: !state.showControls };

    case 'SET_CONTROLS_VISIBLE':
      return { ...state, showControls: action.visible };

    case 'TOGGLE_FULLSCREEN':
      return { ...state, isFullscreen: !state.isFullscreen };

    case 'SET_FULLSCREEN':
      return { ...state, isFullscreen: action.isFullscreen };

    case 'SET_BUFFERING':
      return { ...state, isBuffering: action.isBuffering };

    case 'STOP':
      return {
        ...initialPlaybackState,
        volume: state.volume,
        isMuted: state.isMuted,
        speed: state.speed,
      };

    case 'ERROR':
      return {
        ...state,
        status: PlaybackStatus.Error,
        errorMessage: action.message,
        showControls: true,
      };

    case 'AUDIO_ERROR':
      return {
        ...state,
        audioError: action.message,
        isBuffering: false,
      };

    case 'CLEAR_AUDIO_ERROR':
      return {
        ...state,
        audioError: undefined,
      };

    case 'RESET':
      return initialPlaybackState;

    default:
      return state;
  }
}

// Context types
interface PlayerContextType {
  state: PlaybackState;
  dispatch: React.Dispatch<PlayerAction>;
  // Convenience actions
  play: () => void;
  pause: () => void;
  togglePlayPause: () => void;
  seekTo: (position: Duration) => void;
  setVolume: (volume: number) => void;
  toggleMute: () => void;
  setSpeed: (speed: number) => void;
  nextClip: () => void;
  previousClip: () => void;
  jumpToClip: (clipIndex: number, sceneIndex?: number) => void;
  nextScene: () => void;
  previousScene: () => void;
  jumpToScene: (sceneIndex: number) => void;
  loadContent: (playerItem: PlayerItem) => void;
  stop: () => void;
  setError: (message: string) => void;
}

const PlayerContext = createContext<PlayerContextType | undefined>(undefined);

interface PlayerProviderProps {
  children: ReactNode;
}

export function PlayerProvider({ children }: PlayerProviderProps) {
  const [state, dispatch] = useReducer(playerReducer, initialPlaybackState);

  const play = useCallback(() => dispatch({ type: 'PLAY' }), []);
  const pause = useCallback(() => dispatch({ type: 'PAUSE' }), []);
  const togglePlayPause = useCallback(
    () => dispatch({ type: 'TOGGLE_PLAY_PAUSE' }),
    []
  );
  const seekTo = useCallback(
    (position: Duration) => dispatch({ type: 'SEEK_TO', position }),
    []
  );
  const setVolume = useCallback(
    (volume: number) => dispatch({ type: 'SET_VOLUME', volume }),
    []
  );
  const toggleMute = useCallback(() => dispatch({ type: 'TOGGLE_MUTE' }), []);
  const setSpeed = useCallback(
    (speed: number) => dispatch({ type: 'SET_SPEED', speed }),
    []
  );
  const nextClip = useCallback(() => dispatch({ type: 'NEXT_CLIP' }), []);
  const previousClip = useCallback(
    () => dispatch({ type: 'PREVIOUS_CLIP' }),
    []
  );
  const jumpToClip = useCallback(
    (clipIndex: number, sceneIndex?: number) =>
      dispatch({ type: 'JUMP_TO_CLIP', clipIndex, sceneIndex }),
    []
  );
  const nextScene = useCallback(() => dispatch({ type: 'NEXT_SCENE' }), []);
  const previousScene = useCallback(
    () => dispatch({ type: 'PREVIOUS_SCENE' }),
    []
  );
  const jumpToScene = useCallback(
    (sceneIndex: number) => dispatch({ type: 'JUMP_TO_SCENE', sceneIndex }),
    []
  );
  const loadContent = useCallback(
    (playerItem: PlayerItem) =>
      dispatch({ type: 'CONTENT_LOADED', playerItem }),
    []
  );
  const stop = useCallback(() => dispatch({ type: 'STOP' }), []);
  const setError = useCallback(
    (message: string) => dispatch({ type: 'ERROR', message }),
    []
  );

  const value: PlayerContextType = {
    state,
    dispatch,
    play,
    pause,
    togglePlayPause,
    seekTo,
    setVolume,
    toggleMute,
    setSpeed,
    nextClip,
    previousClip,
    jumpToClip,
    nextScene,
    previousScene,
    jumpToScene,
    loadContent,
    stop,
    setError,
  };

  return (
    <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>
  );
}

export function usePlayer(): PlayerContextType {
  const context = useContext(PlayerContext);
  if (context === undefined) {
    throw new Error('usePlayer must be used within a PlayerProvider');
  }
  return context;
}

export default PlayerContext;
